import { isValidPhoneNumber } from "@ews-admin/global-design-system";
import { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { isCompletePhoneNumber } from "../utils/phone.utils";
import {
  CivilityEnum,
  NotificationPrefEnum,
  RoleEnum,
} from "../types/user.definition";

// Helper function to validate age
const validateAge = (dateString: string, minAge: number): boolean => {
  const birthDate = new Date(dateString);
  const today = new Date();
  const age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  const dayDiff = today.getDate() - birthDate.getDate();

  const actualAge =
    monthDiff < 0 || (monthDiff === 0 && dayDiff < 0) ? age - 1 : age;

  return actualAge >= minAge;
};

export const secretaryFormSchema = (
  t: TFunction,
  isEditMode: boolean = false
) =>
  z
    .object({
      email: z
        .string()
        .nonempty(t("common.validation.required"))
        .email(t("common.validation.invalidEmail")),
      password: z
        .string()
        .optional()
        .refine(
          (val) => {
            if (val && val.trim() !== "") {
              return val.length >= 8;
            }
            return true;
          },
          { message: t("common.validation.passwordTooShort") }
        )
        .refine(
          (val) => {
            if (val && val.trim() !== "") {
              return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(val);
            }
            return true;
          },
          { message: t("common.validation.passwordComplexity") }
        ),
      confirmPassword: z.string().optional(),
      firstName: z.string().nonempty(t("common.validation.required")),
      lastName: z.string().nonempty(t("common.validation.required")),
      civility: z.enum([CivilityEnum.MR, CivilityEnum.MRS], {
        message: t("common.validation.required"),
      }),
      phoneNumber: z.string().nonempty(t("common.validation.required")).refine(
        (val) => (isEditMode ? isValidPhoneNumber(val) : isCompletePhoneNumber(val)),
        { message: t("common.validation.invalidPhoneNumber") }
      ),
      dateOfBirth: z
        .string()
        .nonempty(t("common.validation.required"))
        .refine((date) => {
          const parsedDate = new Date(date);
          return !isNaN(parsedDate.getTime()) && parsedDate < new Date();
        }, t("common.validation.dateOfBirthInThePast"))
        .refine((date) => validateAge(date, 18), {
          message: t("common.validation.minimumAge", { age: 18 }),
        })
        .refine((date) => validateAge(date, 0) && !validateAge(date, 100), {
          message: t("common.validation.invalidDateOfBirth"),
        }),
      roleName: z.literal(RoleEnum.SECRETARY),
      preferredNotificationMethods: z
        .array(z.enum([NotificationPrefEnum.EMAIL, NotificationPrefEnum.SMS]))
        .min(1, t("common.validation.selectAtLeastOne")),
      healthcareFacilityId: z.number().optional(),
    })
    .refine(
      (data) => {
        // Only validate password match if password is provided
        if (data.password && data.password.trim() !== "") {
          return data.password === data.confirmPassword;
        }
        return true;
      },
      {
        message: t("common.validation.passwordMismatch"),
        path: ["confirmPassword"],
      }
    );

export const useSecretaryFormSchema = () => {
  const { t } = useTranslation();
  return secretaryFormSchema(t);
};

export const editSecretaryFormSchema = (t: TFunction) =>
  z
    .object({
      email: z
        .string()
        .nonempty(t("common.validation.required"))
        .email(t("common.validation.invalidEmail")),
      password: z
        .string()
        .optional()
        .refine(
          (val) => {
            if (val && val.trim() !== "") {
              return val.length >= 8;
            }
            return true;
          },
          { message: t("common.validation.passwordTooShort") }
        )
        .refine(
          (val) => {
            if (val && val.trim() !== "") {
              return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(val);
            }
            return true;
          },
          { message: t("common.validation.passwordComplexity") }
        ),
      confirmPassword: z.string().optional(),
      firstName: z.string().nonempty(t("common.validation.required")),
      lastName: z.string().nonempty(t("common.validation.required")),
      civility: z.enum([CivilityEnum.MR, CivilityEnum.MRS], {
        message: t("common.validation.required"),
      }),
      phoneNumber: z.string().nonempty(t("common.validation.required")).refine(
        (val) => isValidPhoneNumber(val),
        { message: t("common.validation.invalidPhoneNumber") }
      ),
      dateOfBirth: z
        .string()
        .nonempty(t("common.validation.required"))
        .refine((date) => {
          const parsedDate = new Date(date);
          return !isNaN(parsedDate.getTime()) && parsedDate < new Date();
        }, t("common.validation.dateOfBirthInThePast"))
        .refine((date) => validateAge(date, 18), {
          message: t("common.validation.minimumAge", { age: 18 }),
        }),
      roleName: z.literal(RoleEnum.SECRETARY),
      preferredNotificationMethods: z
        .array(z.enum([NotificationPrefEnum.EMAIL, NotificationPrefEnum.SMS]))
        .min(1, t("common.validation.selectAtLeastOne")),
      healthcareFacilityId: z.number().optional(),
    })
    .refine(
      (data) => {
        // Only validate password match if password is provided
        if (data.password && data.password.trim() !== "") {
          return data.password === data.confirmPassword;
        }
        return true;
      },
      {
        message: t("common.validation.passwordMismatch"),
        path: ["confirmPassword"],
      }
    );

export const useEditSecretaryFormSchema = () => {
  const { t } = useTranslation();
  return editSecretaryFormSchema(t);
};

export type SecretaryFormSchema = z.infer<
  ReturnType<typeof useSecretaryFormSchema>
>;

export type EditSecretaryFormSchema = z.infer<
  ReturnType<typeof useEditSecretaryFormSchema>
>;
